<script>
  import TabNav from './tab-nav';

  export default {
    name: 'ZmTabs',

    components: {
      TabNav
    },

    props: {
      type: String,
      activeName: String,
      closable: Boolean,
      addable: Boolean,
      value: {},
      editable: Boolean,
      tabPosition: {
        type: String,
        default: 'top'
      },
      beforeLeave: Function,
      stretch: Boolean,
      ariaLabel: String
    },

    provide() {
      return {
        rootTabs: this
      };
    },

    data() {
      return {
        currentName: this.value || this.activeName,
        panes: []
      };
    },

    watch: {
      activeName(value) {
        this.setCurrentName(value);
      },
      value(value) {
        this.setCurrentName(value);
      },
      currentName(value) {
        if (this.$refs.nav) {
          this.$nextTick(() => {
            this.$refs.nav.$nextTick(_ => {
              this.$refs.nav.scrollToActiveTab();
            });
          });
        }
      }
    },

    methods: {
      calcPaneInstances() {
        if (this.$slots.default) {
          const paneSlots = this.$slots.default.filter(vnode => vnode.tag &&
            vnode.componentOptions && vnode.componentOptions.Ctor.options.name === 'ZmTabPane');
          // update indeed
          const panes = paneSlots.map(({ componentInstance }) => componentInstance);
          if (!(panes.length === this.panes.length && panes.every((pane, index) => pane === this.panes[index]))) {
            this.panes = panes;
          }
        }
      },
      handleTabClick(tab, tabName, event) {
        if (tab.disabled) return;
        this.setCurrentName(tabName);
        this.$emit('tab-click', tab, event);
      },
      handleTabEnter(tab, tabName, event) {
        if (tab.disabled) return;
        this.setCurrentName(tabName);
        this.$emit('tab-enter', tab, event);
      },
      setCurrentName(value) {
        const changeCurrentName = () => {
          this.currentName = value;
          this.$emit('input', value);
        };
        if (this.currentName !== value && this.beforeLeave) {
          const before = this.beforeLeave(value, this.currentName);
          if (before && before.then) {
            before.then(() => {
              changeCurrentName();

              this.$refs.nav && this.$refs.nav.removeFocus();
            }, () => {
              // ignore promise rejection in `before-leave` hook
            });
          } else if (before !== false) {
            changeCurrentName();
          }
        } else {
          changeCurrentName();
        }
      },
      setActiveName(value) {
        this.setCurrentName(value);
        this.$refs.nav && this.$refs.nav.$el.querySelector('[id=tab-' + value + ']') && this.$refs.nav.$el.querySelector('[id=tab-' + value + ']').focus();
      }
    },
    render(h) {
      let {
        type,
        handleTabClick,
        handleTabEnter,
        currentName,
        panes,
        tabPosition,
        stretch,
        ariaLabel
      } = this;

      const navData = {
        props: {
          currentName,
          onTabClick: handleTabClick,
          onTabEnter: handleTabEnter,
          type,
          panes,
          stretch,
          ariaLabel
        },
        ref: 'nav'
      };
      const header = (
        <div class={['zm-tabs__header', `is-${tabPosition}`]}>
          <tab-nav {...navData}></tab-nav>
        </div>
      );
      const panels = (
        <div class="zm-tabs__content">
          {this.$slots.default}
        </div>
      );

      return (
        <div class={{
          'zm-tabs': true,
          'zm-tabs--card': type === 'card',
          'zm-tabs--capsule': type === 'capsule',
          'zm-tabs--pipeline': type === 'pipeline',
          [`zm-tabs--${tabPosition}`]: true,
          'zm-tabs--border-card': type === 'border-card'
        }}>
          {tabPosition !== 'bottom' ? [header, panels] : [panels, header]}
        </div>
      );
    },

    created() {
      if (!this.currentName) {
        this.setCurrentName('0');
      }
    },

    mounted() {
      this.calcPaneInstances();
    },

    updated() {
      this.calcPaneInstances();
    }
  };
</script>

<style lang="scss">
  @import "../../scss/mixins/mixins";
  @import "../../scss/common/var";

  @include b(tabs) {
    @include e(header) {
      padding: 0;
      position: relative;
      margin: 0 0 15px;
    }
    @include e(active-bar) {
      position: absolute;
      bottom: 0;
      left: 0;
      height: 2px;
      background-color: $--color-primary;
      z-index: 1;
      transition: transform .3s cubic-bezier(.645, .045, .355, 1);
      list-style: none;
    }
    @include e(new-tab) {
      float: right;
      border: 1px solid #d3dce6;
      height: 18px;
      width: 18px;
      line-height: 18px;
      margin: 12px 0 9px 10px;
      border-radius: 3px;
      text-align: center;
      font-size: 12px;
      color: #d3dce6;
      cursor: pointer;
      transition: all .15s;

      .zm-icon-plus {
        transform: scale(0.8, 0.8);
      }

      &:hover {
        color: $--color-primary;
      }
    }
    @include e(nav-wrap) {
      overflow: hidden;
      margin-bottom: -1px;
      position: relative;

      &::after {
        content: "";
        position: absolute;
        left: 0;
        bottom: 0;
        width: 100%;
        height: 0;
        // background-color: $--color-background-secondary;
        border: 1px solid $--color-background-secondary;
        z-index: $--index-normal;
      }

      @include when(scrollable) {
        padding: 0 32px;
        box-sizing: border-box;
      }
    }
    @include e(nav-scroll) {
      overflow: hidden;
    }
    @include e((nav-next, nav-prev)) {
      position: absolute;
      top: 4px;
      cursor: pointer;
      line-height: 28px;
      width: 30px;
      font-size: 20px;
      text-align: center;
      color: $--color-text-secondary;
      &:hover {
        color: $--color-primary;
      }
    }
    @include e(nav-next) {
      right: 0;
    }
    @include e(nav-prev) {
      left: 0;
    }
    @include e(nav) {
      white-space: nowrap;
      position: relative;
      transition: transform .3s;
      float: left;
      z-index: #{$--index-normal + 1};

      @include when(stretch) {
        min-width: 100%;
        display: flex;
        & > * {
          flex: 1;
          text-align: center;
        }
      }
    }
    @include e(item) {
      padding: 8px 3px;
      box-sizing: border-box;
      line-height: 40px;
      display: inline-block;
      list-style: none;
      font-size: 18px;
      font-weight: 500;
      color: $--color-text-secondary;
      position: relative;
      margin-right: 24px;
      &:last-child {
        margin-right: 0;
      }
      &:focus, &:focus:active {
        outline: none;
      }

      &:after {
        content: "";
        position: absolute;
        bottom: 0;
        left: 0;
        width: 100%;
        height: 2px;
        background-color: transparent;
      }

      /*&:focus.is-active.is-focus:not(:active) {
        box-shadow: 0 0 2px 2px #409eff inset;
        border-radius: 3px;
      }*/

      & .zm-icon-close {
        border-radius: 50%;
        text-align: center;
        transition: all .3s cubic-bezier(.645, .045, .355, 1);
        margin-left: 5px;
        &:before {
          transform: scale(.9);
          display: inline-block;
        }

        &:hover {
          background-color: $--color-text-placeholder;
          color: $--color-white;
        }
      }
      & [class*=" zm-icon-"],
      & [class^=zm-icon-] {
        font-size: 14px;
      }
      @include when(active) {
        color: $--color-primary;
        &:after {
          background-color: $--color-primary;
        }
      }

      &:hover {
        color: $--color-primary;
        cursor: pointer;
      }

      @include when(disabled) {
        color: $--disabled-color-base;
        cursor: default;
      }
    }
    @include e(content) {
      overflow: hidden;
      position: relative;
    }
    @include m(card) {
      > .zm-tabs__header {
        border-bottom: 1px solid $--border-color-light;
      }
      > .zm-tabs__header .zm-tabs__nav-wrap::after {
        content: none;
      }
      > .zm-tabs__header .zm-tabs__nav {
        border: 1px solid $--border-color-light;
        border-bottom: none;
        border-radius: 4px 4px 0 0;
        box-sizing: border-box;
      }
      > .zm-tabs__header .zm-tabs__active-bar {
        display: none;
      }
      > .zm-tabs__header .zm-tabs__item .zm-icon-close {
        position: relative;
        font-size: 12px;
        width: 0;
        height: 14px;
        vertical-align: middle;
        line-height: 15px;
        overflow: hidden;
        top: -1px;
        right: -2px;
        transform-origin: 100% 50%;
      }
      > .zm-tabs__header .zm-tabs__item {
        border-bottom: 1px solid transparent;
        border-left: 1px solid $--border-color-light;
        transition: color .3s cubic-bezier(.645, .045, .355, 1), padding .3s cubic-bezier(.645, .045, .355, 1);
        margin-right: 0;
        padding-left: 24px;
        padding-right: 24px;
        font-size: 18px;
        line-height: 32px;
        &:first-child {
          border-left: none;
        }
        &.is-closable {
          &:hover {
            padding-left: 13px;
            padding-right: 13px;

            & .zm-icon-close {
              width: 14px;
            }
          }
        }
        &.is-active {
          border-bottom-color: $--color-white;
          &:after {
            background-color: transparent;
          }
          &.is-closable {
            padding-left: 20px;
            padding-right: 20px;

            .zm-icon-close {
              width: 14px;
            }
          }
        }
      }
    }
    @include m(capsule) {
      > .zm-tabs__header .zm-tabs__nav-wrap::after {
        content: none;
      }
      > .zm-tabs__header .zm-tabs__nav {
        border-radius: 8px;
        box-sizing: border-box;
      }
      > .zm-tabs__header .zm-tabs__nav-prev,
      > .zm-tabs__header .zm-tabs__nav-next {
        top: 0;
        line-height: $--button-height;
      }
      > .zm-tabs__header .zm-tabs__active-bar {
        display: none;
      }
      > .zm-tabs__header .zm-tabs__item .zm-icon-close {
        position: relative;
        font-size: 12px;
        width: 0;
        height: 14px;
        vertical-align: middle;
        line-height: 15px;
        overflow: hidden;
        top: -1px;
        right: -2px;
        transform-origin: 100% 50%;
      }
      > .zm-tabs__header .zm-tabs__item {
        transition: color .3s cubic-bezier(.645, .045, .355, 1), padding .3s cubic-bezier(.645, .045, .355, 1);
        line-height: $--button-height;
        padding: 0 $--button-padding-horizontal;
        font-size: $--button-font-size;
        border-radius: $--button-border-radius;
        &:first-child {
          border-left: none;
        }
        &.is-closable {
          &:hover {
            padding-left: 13px;
            padding-right: 13px;

            & .zm-icon-close {
              width: 14px;
            }
          }
        }
        &.is-active {
          background-color: #E7F1FD;
          color: #0862d1;
          &.is-closable {
            padding-left: 20px;
            padding-right: 20px;

            .zm-icon-close {
              width: 14px;
            }
          }
          &::after {
            display: none;
          }
        }
      }
    }

    @include m(pipeline) {
      > .zm-tabs__header .zm-tabs__nav-wrap::after {
        content: none;
      }
      > .zm-tabs__header .zm-tabs__nav {
        border-radius: 8px;
        box-sizing: border-box;
        background: $--color-background-secondary;
      }
      > .zm-tabs__header .zm-tabs__nav-prev,
      > .zm-tabs__header .zm-tabs__nav-next {
        top: 0;
        line-height: $--button-height;
      }
      > .zm-tabs__header .zm-tabs__active-bar {
        display: none;
      }
      > .zm-tabs__header .zm-tabs__item .zm-icon-close {
        position: relative;
        font-size: 12px;
        width: 0;
        height: 14px;
        vertical-align: middle;
        line-height: 15px;
        overflow: hidden;
        top: -1px;
        right: -2px;
        transform-origin: 100% 50%;
      }
      > .zm-tabs__header .zm-tabs__item {
        transition: color .3s cubic-bezier(.645, .045, .355, 1), padding .3s cubic-bezier(.645, .045, .355, 1);
        line-height: $--button-height;
        padding: 0 $--button-padding-horizontal;
        font-size: $--button-font-size;
        border-radius: $--button-border-radius;
        margin-right: 0;
        &:first-child {
          border-left: none;
        }
        &.is-closable {
          &:hover {
            padding-left: 13px;
            padding-right: 13px;

            & .zm-icon-close {
              width: 14px;
            }
          }
        }
        &.is-active {
          background-color: $--color-white;
          color: #0862d1;
          box-shadow: 0 1px 4px rgba(0, 0, 0, 0.12);
          &.is-closable {
            padding-left: 20px;
            padding-right: 20px;

            .zm-icon-close {
              width: 14px;
            }
          }
          &::after {
            display: none;
          }
        }
      }
      > .zm-tabs__header .zm-tabs__nav-scroll {
        display: flex;
        justify-content: center;
        overflow: visible;
      }
      > .zm-tabs__header .zm-tabs__nav-wrap {
        overflow: visible;
      }
    }

    @include m((top, bottom)) {
      /* .zm-tabs__item.is-top:nth-child(2),
      .zm-tabs__item.is-bottom:nth-child(2) {
        padding-left: 0;
      }
      .zm-tabs__item.is-top:last-child,
      .zm-tabs__item.is-bottom:last-child {
        padding-right: 0;
      } */

      /* &.zm-tabs--border-card, &.zm-tabs--card,
       .zm-tabs--left, .zm-tabs--right {
         .zm-tabs__item:nth-child(2) {
           padding-left: 20px;
         }
         .zm-tabs__item:last-child {
           padding-right: 20px;
         }
       }*/
    }
    @include m(bottom) {
      .zm-tabs__header.is-bottom {
        margin-bottom: 0;
        margin-top: 10px;
      }
      &.zm-tabs--border-card {
        .zm-tabs__header.is-bottom {
          border-bottom: 0;
          border-top: 1px solid $--border-color-base;
        }
        .zm-tabs__nav-wrap.is-bottom {
          margin-top: -1px;
          margin-bottom: 0;
        }
        .zm-tabs__item.is-bottom:not(.is-active) {
          border: 1px solid transparent;
        }
        .zm-tabs__item.is-bottom {
          margin: 0 -1px -1px -1px;
        }
      }
    }
    @include m((left, right)) {
      overflow: hidden;

      .zm-tabs__header.is-left,
      .zm-tabs__header.is-right,
      .zm-tabs__nav-wrap.is-left,
      .zm-tabs__nav-wrap.is-right,
      .zm-tabs__nav-scroll {
        height: 100%;
      }

      .zm-tabs__active-bar.is-left,
      .zm-tabs__active-bar.is-right {
        top: 0;
        bottom: auto;
        width: 2px;
        height: auto;
      }

      .zm-tabs__nav-wrap.is-left,
      .zm-tabs__nav-wrap.is-right {
        margin-bottom: 0;

        > .zm-tabs__nav-prev,
        > .zm-tabs__nav-next {
          height: 30px;
          line-height: 30px;
          width: 100%;
          text-align: center;
          cursor: pointer;

          i {
            transform: rotateZ(90deg);
          }
        }
        > .zm-tabs__nav-prev {
          left: auto;
          top: 0;
        }
        > .zm-tabs__nav-next {
          right: auto;
          bottom: 0;
        }

        &.is-scrollable {
          padding: 30px 0;
        }

        &::after {
          height: 100%;
          width: 2px;
          bottom: auto;
          top: 0;
        }
      }

      .zm-tabs__nav.is-left,
      .zm-tabs__nav.is-right {
        float: none;
      }
      .zm-tabs__item.is-left,
      .zm-tabs__item.is-right {
        display: block;
      }
    }
    @include m(left) {
      .zm-tabs__header.is-left {
        float: left;
        margin-bottom: 0;
        margin-right: 10px;
      }
      .zm-tabs__nav-wrap.is-left {
        margin-right: -1px;
        &::after {
          left: auto;
          right: 0;
        }
      }
      .zm-tabs__active-bar.is-left {
        right: 0;
        left: auto;
      }
      .zm-tabs__item.is-left {
        text-align: right;
      }

      &.zm-tabs--card {
        .zm-tabs__active-bar.is-left {
          display: none;
        }
        .zm-tabs__item.is-left {
          border-left: none;
          border-right: 1px solid $--border-color-light;
          border-bottom: none;
          border-top: 1px solid $--border-color-light;
        }
        .zm-tabs__item.is-left:first-child {
          border-right: 1px solid $--border-color-light;
          border-top: none;
        }
        .zm-tabs__item.is-left.is-active {
          border: 1px solid $--border-color-light;
          border-right-color: #fff;
          border-left: none;
          border-bottom: none;

          &:first-child {
            border-top: none;
          }
          &:last-child {
            border-bottom: none;
          }
        }

        .zm-tabs__nav {
          border-radius: 4px 0 0 4px;
          border-bottom: 1px solid $--border-color-light;
          border-right: none;
        }

        .zm-tabs__new-tab {
          float: none;
        }
      }

      &.zm-tabs--border-card {
        .zm-tabs__header.is-left {
          border-right: 1px solid #dfe4ed;
        }
        .zm-tabs__item.is-left {
          border: 1px solid transparent;
          margin: -1px 0 -1px -1px;

          &.is-active {
            border-color: transparent;
            border-top-color: rgb(209, 219, 229);
            border-bottom-color: rgb(209, 219, 229);
          }
        }
      }
    }
    @include m(right) {
      .zm-tabs__header.is-right {
        float: right;
        margin-bottom: 0;
        margin-left: 10px;
      }

      .zm-tabs__nav-wrap.is-right {
        margin-left: -1px;
        &::after {
          left: 0;
          right: auto;
        }
      }

      .zm-tabs__active-bar.is-right {
        left: 0;
      }

      &.zm-tabs--card {
        .zm-tabs__active-bar.is-right {
          display: none;
        }
        .zm-tabs__item.is-right {
          border-bottom: none;
          border-top: 1px solid $--border-color-light;
        }
        .zm-tabs__item.is-right:first-child {
          border-left: 1px solid $--border-color-light;
          border-top: none;
        }
        .zm-tabs__item.is-right.is-active {
          border: 1px solid $--border-color-light;
          border-left-color: #fff;
          border-right: none;
          border-bottom: none;

          &:first-child {
            border-top: none;
          }
          &:last-child {
            border-bottom: none;
          }
        }

        .zm-tabs__nav {
          border-radius: 0 4px 4px 0;
          border-bottom: 1px solid $--border-color-light;
          border-left: none;
        }
      }
      &.zm-tabs--border-card {
        .zm-tabs__header.is-right {
          border-left: 1px solid #dfe4ed;
        }
        .zm-tabs__item.is-right {
          border: 1px solid transparent;
          margin: -1px -1px -1px 0;

          &.is-active {
            border-color: transparent;
            border-top-color: rgb(209, 219, 229);
            border-bottom-color: rgb(209, 219, 229);
          }
        }
      }
    }
  }
  @media (forced-colors: active) {
    .zm-tabs__item.is-active {
      outline: dotted thin !important;
      outline-offset: -2px;
      &:after{
        display: none;
      }
    }
  }
  .is-keyboard-event .zm-tabs__item:focus,
  .is-keyboard-event .zm-tabs__item:focus:active,
  .is-keyboard-event .zm-tabs--pipeline > .zm-tabs__header .zm-tabs__item.is-active:focus {
    box-shadow: 0 0 2px 2px #409eff inset;
  }

  .slideInRight-transition,
  .slideInLeft-transition {
    display: inline-block;
  }

  .slideInRight-enter {
    animation: slideInRight-enter .3s;
  }

  .slideInRight-leave {
    position: absolute;
    left: 0;
    right: 0;
    animation: slideInRight-leave .3s;
  }

  .slideInLeft-enter {
    animation: slideInLeft-enter .3s;
  }

  .slideInLeft-leave {
    position: absolute;
    left: 0;
    right: 0;
    animation: slideInLeft-leave .3s;
  }

  @keyframes slideInRight-enter {
    0% {
      opacity: 0;
      -webkit-transform-origin: 0 0;
      transform-origin: 0 0;
      -webkit-transform: translateX(100%);
      transform: translateX(100%)
    }

    to {
      opacity: 1;
      -webkit-transform-origin: 0 0;
      transform-origin: 0 0;
      -webkit-transform: translateX(0);
      transform: translateX(0)
    }
  }

  @keyframes slideInRight-leave {
    0% {
      -webkit-transform-origin: 0 0;
      transform-origin: 0 0;
      -webkit-transform: translateX(0);
      transform: translateX(0);
      opacity: 1
    }

    100% {
      -webkit-transform-origin: 0 0;
      transform-origin: 0 0;
      -webkit-transform: translateX(100%);
      transform: translateX(100%);
      opacity: 0
    }
  }

  @keyframes slideInLeft-enter {
    0% {
      opacity: 0;
      -webkit-transform-origin: 0 0;
      transform-origin: 0 0;
      -webkit-transform: translateX(-100%);
      transform: translateX(-100%)
    }

    to {
      opacity: 1;
      -webkit-transform-origin: 0 0;
      transform-origin: 0 0;
      -webkit-transform: translateX(0);
      transform: translateX(0)
    }
  }

  @keyframes slideInLeft-leave {
    0% {
      -webkit-transform-origin: 0 0;
      transform-origin: 0 0;
      -webkit-transform: translateX(0);
      transform: translateX(0);
      opacity: 1
    }

    100% {
      -webkit-transform-origin: 0 0;
      transform-origin: 0 0;
      -webkit-transform: translateX(-100%);
      transform: translateX(-100%);
      opacity: 0
    }
  }
</style>
